import moment from 'moment-timezone'
import type { PiniaCustomStateProperties } from 'pinia'

import router from '@/router'

import type { OpportunityData } from '../interfaces/opportunities'

import { caseStatus } from './enums'

// 👉 IsEmpty
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === '')
    return true

  return !!(Array.isArray(value) && value.length === 0)
}

// get key within object else return default
export const get = function (
  obj:
    | ({ entity: any } & PiniaCustomStateProperties<{ entity: any }>)
    | ({ error: null; isFetching: boolean; data: any } & PiniaCustomStateProperties<{
        error: null
        isFetching: boolean
        data: any
      }>)
    | ({
        error: string
        isFetching: boolean
        list: never[]
        stats: any
        meta: { total: number }
      } & PiniaCustomStateProperties<{
        error: string
        isFetching: boolean
        list: never[]
        stats: any
        meta: { total: number }
      }>),
  key: string,
  empty: never[] | null,
) {
  return (
    key.split('.').reduce((o: { [x: string]: any } | null, x: string | number) => {
      return (typeof o === 'undefined' || o === null) ? o : o[x]
    }, obj) || empty
  )
}

/**
 *
 * @param uuid
 * @description - It takes a uuid and returns the last 6 characters of the uuid in uppercase to make it more readable
 * @returns
 */
export function readableUuid(uuid: string) {
  return `#${uuid.replace(/-/g, '').slice(-6).toUpperCase()}`
}

/**
 * It takes a date object and returns a string in the format of "MM/DD/YYYY"
 * @param date - The date object to format
 * @returns The month, day, and year of the date.
 */
export function simpleDateFormat(rawDate: any, ignoreTimezone = false) {
  if (!rawDate)
    return null

  if (ignoreTimezone)
    return moment.utc(rawDate).format('MM/DD/YYYY')

  return moment.utc(rawDate).local().format('MM/DD/YYYY')
}

/**
 * This function creates a link element, sets its href to the upload URL of the attachment,
 * sets the download attribute to the file name of the attachment, appends it to the document body,
 * clicks the link to trigger the download, and then removes the link from the document body.
 * @param {Attachment} item - The item to download.
 */
export function handleFileDownload(item: { upload_url: string; file_name?: string | null }) {
  const link = document.createElement('a')

  link.href = item.upload_url
  link.download = item.file_name || 'download'
  link.target = '_blank'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Formats a date as e.g. "Mar 23, 2025 3:45 PM" (local or UTC per `local`).
 * @param seconds When true, includes seconds in the time portion.
 */
export function simpleDatetimeFormat(date: any, local = true, seconds = false) {
  if (!date)
    return null

  const time = moment.utc(date)

  if (local)
    time.local()

  return time.format(seconds ? 'MMM D, YYYY h:mm:ss A' : 'MMM D, YYYY h:mm A')
}

/**
 * If the value of a key is null, delete the key.
 * @param fullObj - The full object that you want to clean.
 * @returns the object with the null values removed.
 */
export const cleanDictionary = function (fullObj: any) {
  const obj = { ...fullObj }

  Object.keys(obj).forEach(key => {
    if (isEmpty(obj[key]))
      delete obj[key]
  })

  return obj
}

/**
 * It takes a number and returns a string formatted as US currency
 * @param val - The value to format
 * @returns A function that takes a value and returns a formatted string.
 */
export function currencyFormat(val: any, decimals = 2, showSymbol = true) {
  if (!val)
    return '$0.00'

  val = val.toString().replace(/,/g, '')

  if (Math.abs(parseFloat(val)) < 0.01)
    decimals = 4

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
  })

  let result = formatter.format(val)

  if (!showSymbol)
    result = result.replace(/^\$/, '') // Removes the dollar sign from the beginning of the string

  return result
}

/**
 * It takes a key, a value, and a route, and returns a new route with the query parameter updated.
 * @param key - the key of the query param you want to update
 * @param val - the value of the query param
 * @param route - the route you want to update the query params on
 */

/**
 * Update query parameters in the URL with a single router.push
 *
 * Merges with existing query params to preserve pagination/sort/etc.
 *
 * @param updates - Object with key-value pairs to update (undefined values remove the param)
 */
export function updateQueryParamsFromObject(
  updates: Record<string, string | string[] | null | undefined>,
) {
  // Start with existing query params to preserve pagination/sort/etc
  const query: Record<string, string | string[]> = {
    ...(router.currentRoute.value.query as Record<string, string | string[]>),
  }

  for (const [key, value] of Object.entries(updates)) {
    if (Array.isArray(value)) {
      if (value.length > 0)
        query[key] = value
      else delete query[key] // Remove empty arrays
    } else if (value !== null && value !== undefined && value !== '') {
      query[key] = value
    } else {
      delete query[key] // Remove null/undefined/empty values
    }
  }

  router.push({
    path: router.currentRoute.value.path,
    query,
  })
}

/**
 * It takes a string of numbers and returns a formatted phone number
 * @param phoneNumberString - The phone number string to format.
 * @returns the phone number in the format of (xxx) xxx-xxxx
 */
export const formatPhoneNumber = function (phoneNumberString: string | number): string | null {
  const cleaned = `${phoneNumberString}`.replace(/\D/g, '')
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    const intlCode = match[1] ? '+1 ' : ''

    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
  }

  return cleaned
}

/* The above TypeScript code defines a function `haveParamsChanged` that takes two parameters `params1`
and `params2`. It checks if `params1` is falsy, it returns `true` indicating that the parameters
have changed. It then converts the entries of both `params1` and `params2` into arrays and compares
their lengths. If the lengths are not equal, it returns `true`. */

/**
 * It takes a dictionary of filters and returns a query string that can be used as a querystring for server filters
 * @param data - {
 * @returns A query string
 */
export function buildQueryString(data: object) {
  const qs = new URLSearchParams(cleanDictionary(data))

  for (const [key, value] of Object.entries(data)) {
    if (Array.isArray(value) && value.length > 0) {
      value.forEach(item => {
        qs.append(`${key}[]`, item)
      })
    } else if (value !== null && value !== undefined) {
      qs.set(key, value.toString())
    }
  }

  return qs
}
export function ssnFormat(text: string) {
  const ssn = text.replace(/-/g, '').trim()

  return `${ssn.substring(0, 3)}-${ssn.substring(3, 5)}-${ssn.substring(5)}`
}
export function getStatusColor(status: string) {
  const statusColors: { [key: string]: string } = {
    open: 'success',
    seeking: 'warning',
    closed: 'error',
    temporarily_waitlisting: 'warning',
  }

  return statusColors[status?.trim().toLowerCase()] || 'grey'
}

export function getStatusTitle(statusValue: string) {
  return caseStatus.find(s => s.value === statusValue)?.title || statusValue
}
export function getOpportunityStatusColor(opportunity: OpportunityData) {
  const statusMap: { [key: string]: string } = {
    qualified_waiting: 'warning',
    rejected: 'error',
  }

  if (opportunity.status === 'closed' && opportunity.closed_status === false)
    return 'error'

  return statusMap[opportunity.status?.trim().toLowerCase()] || 'success'
}

export function getOpportunityStatusTitle(opportunity: OpportunityData) {
  if (opportunity.status === 'closed')
    return `Closed ${opportunity.closed_status ? 'Won' : 'Lost'}`

  return opportunity.status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

export function countObject(obj: { [key: string]: any }): number {
  let count = 0
  for (const key in obj) {
    const val = obj[key]
    if (
      (Array.isArray(val) && val.length > 0)
      || (val !== null && val !== undefined && !Array.isArray(val) && val !== '') // truthy primitives
    )
      count++
  }

  return count
}

export function parseSortDirection(sort: string | boolean): string {
  if (typeof sort === 'boolean') {
    if (sort)
      return 'asc'
    else return 'desc'
  }

  return sort.trim().toLowerCase()
}

export function humanizeField(field: string) {
  return field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * Cleans opportunity data by removing closed_lost_reason_id if:
 * - closed_status is not false (i.e., Won or null/undefined)
 * - closed_lost_reason_id is null/undefined
 *
 * Only includes closed_lost_reason_id when status is Lost (closed_status === false) and has a valid value
 * @param data - The opportunity data to clean
 * @returns The cleaned data object
 */
export function cleanOpportunityClosedReasonData(data: {
  closed_status?: boolean | null
  closed_lost_reason_id?: number | null
  [key: string]: any
}): any {
  const cleanedData = { ...data }

  if (cleanedData.closed_status !== false || !cleanedData.closed_lost_reason_id) {
    // Remove closed_lost_reason_id if it's Won (true) or null/undefined
    delete cleanedData.closed_lost_reason_id
  }

  return cleanedData
}

/**
 * Maps charge category enum values to human-readable labels
 * @param category - The category value from the server
 * @returns Human-readable label for the category
 */
export function getChargeCategoryLabel(category: string | null | undefined): string {
  if (!category)
    return '—'

  const categoryMap: Record<string, string> = {
    medication: 'Drug',
    lab: 'Lab Test',
    em: 'Office Visit',
    procedure: 'Procedure',
    facility: 'Facility',
    imaging: 'Imaging',
    supply: 'Supply',
    surgical: 'Surgical',
    unknown: 'Unknown',
    other: 'Other',
  }

  // Try exact match first
  const lowerCategory = category.toLowerCase()
  if (categoryMap[lowerCategory])
    return categoryMap[lowerCategory]

  // Fallback: title case the category
  return category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}
